// Synthesizes legend swatch items from ArcGIS renderer JSON (drawingInfo), for services where no
// /legend REST resource exists — hosted FeatureServers — or where the effective renderer is an
// ArcGIS Online item override the server has never seen. Output items use the exact field shape
// arcgis-legend.mjs's buildLegendSectionHtml consumes ({ label, imageData, contentType, width,
// height }); swatches are base64 SVG data-URI payloads (the site's img-src already allows data:).
// Pure module: no fetch, no DOM.

const SWATCH_SIZE = 20;

// ArcGIS colors are [r, g, b, a] with a in 0-255. Null when absent/malformed (hollow symbols).
function colorToCss(color) {
	if (!Array.isArray(color) || color.length < 3) {
		return null;
	}
	if (!Number.isFinite(color[0]) || !Number.isFinite(color[1]) || !Number.isFinite(color[2])) {
		return null;
	}
	// Alpha rounded to 3 decimals so the CSS stays readable (255-scale rarely divides evenly).
	const alpha = Number.isFinite(color[3]) ? Math.max(0, Math.min(1, Math.round((color[3] / 255) * 1000) / 1000)) : 1;
	return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
}

// Polygon fill (esriSFS). Hatched styles are approximated as solid; esriSFSNull keeps only the
// outline. Null when neither fill nor outline yields a color.
function fillSwatchSvg(symbol) {
	const fill = symbol.style === "esriSFSNull" ? null : colorToCss(symbol.color);
	const outline = symbol.outline != null ? colorToCss(symbol.outline.color) : null;
	if (fill == null && outline == null) {
		return null;
	}
	const strokeWidth = outline != null
		? Math.min(Number.isFinite(symbol.outline.width) && symbol.outline.width > 0 ? symbol.outline.width : 1, 3)
		: 0;
	const inset = Math.max(strokeWidth / 2, 0.5);
	const side = SWATCH_SIZE - inset * 2;
	return `<svg xmlns="http://www.w3.org/2000/svg" width="${SWATCH_SIZE}" height="${SWATCH_SIZE}">`
		+ `<rect x="${inset}" y="${inset}" width="${side}" height="${side}" fill="${fill == null ? "none" : fill}"`
		+ (outline != null ? ` stroke="${outline}" stroke-width="${strokeWidth}"` : "")
		+ `/></svg>`;
}

// Line (esriSLS). Dash/dot styles collapse to one dash pattern — a legend swatch, not cartography.
function lineSwatchSvg(symbol) {
	const stroke = colorToCss(symbol.color);
	if (stroke == null || symbol.style === "esriSLSNull") {
		return null;
	}
	const width = Math.min(Number.isFinite(symbol.width) && symbol.width > 0 ? symbol.width : 1, 4);
	const dash = typeof symbol.style === "string" && /Dash|Dot/i.test(symbol.style) ? ` stroke-dasharray="4 3"` : "";
	return `<svg xmlns="http://www.w3.org/2000/svg" width="${SWATCH_SIZE}" height="${SWATCH_SIZE}">`
		+ `<line x1="1" y1="${SWATCH_SIZE / 2}" x2="${SWATCH_SIZE - 1}" y2="${SWATCH_SIZE / 2}" stroke="${stroke}" stroke-width="${width}"${dash}/></svg>`;
}

// Marker (esriSMS). Every marker style is approximated as a circle — close enough to key a legend.
function markerSwatchSvg(symbol) {
	const fill = colorToCss(symbol.color);
	const outline = symbol.outline != null ? colorToCss(symbol.outline.color) : null;
	if (fill == null && outline == null) {
		return null;
	}
	return `<svg xmlns="http://www.w3.org/2000/svg" width="${SWATCH_SIZE}" height="${SWATCH_SIZE}">`
		+ `<circle cx="${SWATCH_SIZE / 2}" cy="${SWATCH_SIZE / 2}" r="6" fill="${fill == null ? "none" : fill}"`
		+ (outline != null ? ` stroke="${outline}" stroke-width="1.5"` : "")
		+ `/></svg>`;
}

// One legend swatch for an ArcGIS symbol, or null when the symbol type can't be drawn (text
// symbols, 3D symbols, ...). Picture markers already carry a base64 image; vector symbols become
// base64 SVG (markup is pure ASCII, so btoa is safe).
export function symbolToSwatch(symbol) {
	if (symbol == null || typeof symbol !== "object") {
		return null;
	}
	if (symbol.type === "esriPMS") {
		if (typeof symbol.imageData !== "string" || symbol.imageData === "") {
			return null;
		}
		// Authored sizes pass through untouched (proportional-symbol renderers carry meaning in
		// them); fitting oversized SETS to the legend is scaleItemsToBudget's job, where the
		// scale factor can be uniform across the class set. Sub-1 values would truncate to an
		// invisible width="0" and negatives are markup nonsense — both default.
		return {
			imageData: symbol.imageData,
			contentType: typeof symbol.contentType === "string" && symbol.contentType !== "" ? symbol.contentType : "image/png",
			width: Number.isFinite(symbol.width) && symbol.width >= 1 ? Math.trunc(symbol.width) : SWATCH_SIZE,
			height: Number.isFinite(symbol.height) && symbol.height >= 1 ? Math.trunc(symbol.height) : SWATCH_SIZE
		};
	}
	let svg = null;
	if (symbol.type === "esriSFS") {
		svg = fillSwatchSvg(symbol);
	} else if (symbol.type === "esriSLS") {
		svg = lineSwatchSvg(symbol);
	} else if (symbol.type === "esriSMS") {
		svg = markerSwatchSvg(symbol);
	}
	if (svg == null) {
		return null;
	}
	return { imageData: btoa(svg), contentType: "image/svg+xml", width: SWATCH_SIZE, height: SWATCH_SIZE };
}

function row(label, symbol) {
	return { label: typeof label === "string" ? label : "", symbol: symbol };
}

// A proportional-symbol renderer (e.g. USA Wildfires' incident flames, sized by acreage class)
// carries its meaning in RELATIVE swatch sizes, so an oversized set scales uniformly into the
// budget — per-symbol clamping would flatten every class above the cap to one size while smaller
// classes render true, which actively misreads the classification. Within-budget sets keep their
// authored sizes untouched; each symbol keeps its own aspect ratio.
const MAX_SWATCH_SIZE = 30;
// Scaled results floor here so a proportional set's smallest classes stay legible; the floor
// compresses the bottom of the range rather than letting icons shrink into unreadable dots.
// Applies only on the scaling path — a within-budget set keeps authored sizes, however small.
const MIN_SWATCH_SIZE = 16;

function scaleItemsToBudget(items) {
	let maxDimension = 0;
	for (const item of items) {
		maxDimension = Math.max(maxDimension, item.width, item.height);
	}
	if (maxDimension <= MAX_SWATCH_SIZE) {
		return items;
	}
	const factor = MAX_SWATCH_SIZE / maxDimension;
	for (const item of items) {
		item.width = Math.max(MIN_SWATCH_SIZE, Math.round(item.width * factor));
		item.height = Math.max(MIN_SWATCH_SIZE, Math.round(item.height * factor));
	}
	return items;
}

// Legend items for a drawingInfo's renderer: simple -> one row; uniqueValue -> stored order;
// classBreaks -> highest-first (stored order is ascending; Map Viewer displays descending);
// default symbol (when present) last. Rows whose symbol can't be drawn are dropped; [] when
// nothing renderable (callers treat that as legend-unavailable).
export function legendItemsFromDrawingInfo(drawingInfo) {
	const renderer = drawingInfo != null && typeof drawingInfo === "object" ? drawingInfo.renderer : null;
	if (renderer == null || typeof renderer !== "object") {
		return [];
	}
	const rows = [];
	if (renderer.type === "simple") {
		rows.push(row(renderer.label, renderer.symbol));
	} else if (renderer.type === "uniqueValue" && Array.isArray(renderer.uniqueValueInfos)) {
		for (const info of renderer.uniqueValueInfos) {
			if (info != null) {
				rows.push(row(info.label, info.symbol));
			}
		}
	} else if (renderer.type === "classBreaks" && Array.isArray(renderer.classBreakInfos)) {
		for (let i = renderer.classBreakInfos.length - 1; i >= 0; i--) {
			const info = renderer.classBreakInfos[i];
			if (info != null) {
				rows.push(row(info.label, info.symbol));
			}
		}
	} else {
		return [];
	}
	if ((renderer.type === "uniqueValue" || renderer.type === "classBreaks") && renderer.defaultSymbol != null) {
		rows.push(row(renderer.defaultLabel, renderer.defaultSymbol));
	}
	const items = [];
	for (const entry of rows) {
		const swatch = symbolToSwatch(entry.symbol);
		if (swatch != null) {
			items.push({ label: entry.label, imageData: swatch.imageData, contentType: swatch.contentType, width: swatch.width, height: swatch.height });
		}
	}
	return scaleItemsToBudget(items);
}
